import { create } from "zustand";

/** Slide-in token-info drawer state.
 *
 *  One drawer instance is mounted globally in ``Layout``; pages call
 *  ``useTokenDrawer.getState().openFor(...)`` (or the ``useTokenDrawer``
 *  selector hook) to surface details for a clicked entity. The drawer
 *  shows a ``/api/token-lookup?q=...`` payload — list of CEX exchanges,
 *  DEX contracts, mute controls, external links — and additionally
 *  embeds a per-spread block when ``spreadUid`` is set (only used from
 *  Spreads / Dashboard panels where a specific row was clicked).
 *
 *  Why an external store rather than React context: the drawer must
 *  open from leaf rows of virtualised lists (Spreads SignalRow,
 *  Orderbooks GroupHeader, Dashboard panels). Threading callbacks
 *  through ``memo`` boundaries breaks reference equality and forces
 *  re-renders of every row. ``zustand`` lets each row subscribe to
 *  a stable action without coupling its memo deps to drawer state. */

export interface TokenDrawerState {
  open: boolean;
  /** Query passed to ``/api/token-lookup``. Shapes:
   *    ``$ETH``     — CEX symbol search (most common)
   *    ``0xabc...`` — contract address search
   *    ``""``       — empty (drawer hides results until user types). */
  query: string;
  /** When the drawer was opened from a row in Spreads / Dashboard,
   *  this is the spread UID — the drawer will pull the row from
   *  ``useSpreadsStore`` and render the spread-specific block above
   *  the token-info sections. ``null`` when opened from Orderbooks
   *  (no spread context — token info only). */
  spreadUid: string | null;

  openFor: (query: string, spreadUid?: string | null) => void;
  setQuery: (q: string) => void;
  close: () => void;
}

export const useTokenDrawer = create<TokenDrawerState>((set) => ({
  open: false,
  query: "",
  spreadUid: null,

  openFor: (query, spreadUid = null) =>
    set(() => ({ open: true, query, spreadUid })),
  setQuery: (q) => set(() => ({ query: q })),
  close: () => set(() => ({ open: false, spreadUid: null })),
}));
